/**
 * Pricing Approval — the two-level gate on a collab quote.
 *
 * Built on Project Approval's own layout and its own stylesheet, deliberately. Max, 2026-09-10:
 * "cái giao diện này nhiều người vào nên nếu làm như bạn thì rất khó theo dõi". The first version
 * stacked five tables down the page — every section visible at once, each sized against its own rows
 * — and the reader had to scan all of them to find the one line that was theirs. This is tabs with
 * counts and ONE table at a time, which is what the people who use both screens already know.
 *
 * Reusing ProjectApproval.css is the point, not a shortcut: a second visual vocabulary for the same
 * job is the thing that makes a screen hard to follow.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { Pencil, Send, RotateCw, Copy, Trash2, CheckCircle2, Loader2, Undo2, BellRing, Calculator } from 'lucide-react';
import Header from './Header';
import ModuleTabs from './ModuleTabs';
import ModuleHelp from './ModuleHelp';
// The very same dialog Project Approval uses — shared, not copied, so the two cannot drift (Max,
// 2026-09-11: "sửa lại bên project approval khi click approve sẽ hiện ra model như vậy nè").
import { DecisionDialog } from './DecisionDialog';
import { BACKEND_URL } from './backendConfig';
import PricingApprovalDetail from './36_PricingApprovalDetail';
/* The card list and the money format, so the read-back under the decision dialog is priced by the
   SAME call the panel and the calculator are priced by. A second arithmetic here is how the figure
   an approver confirms on comes to differ from the one they read. */
import { cpCards, money } from './cpSummary';
import useAuth from './useAuth';
import './ProjectApproval.css';
import './PricingCalculator.css';

const API = `${BACKEND_URL}/api/pricing/projects`;
const fmtUSD = (v) => (v === null || v === undefined || v === '' ? '—'
  : `$${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const day = (v) => (v ? String(v).slice(0, 10) : '');
// a moment in Vietnam time, yyyy-mm-dd hh:mm — the reminder line and its 4-hour gap are about hours, not days
const hm = (v) => { if (!v) return ''; const x = new Date(new Date(v).getTime() + 7 * 3600000).toISOString(); return `${x.slice(0, 10)} ${x.slice(11, 16)}`; };
const stKey = (s) => `pricing.st${String(s || 'Draft').replace(/\s/g, '')}`;

/* Column widths, in one table so the header, the colgroup and the total cannot drift apart. */
/* The first four are Project Approval's own widths, and they have to be: `.pa-sticky-cols` hard-codes
   the frozen columns' left offsets against them (check 36 -> No at 36 -> the first freeze at 80 -> the
   second at 176). Change one and the frozen columns overlap.

   The rest are generous and the table simply scrolls, which is what that screen does — its own table
   is 2,213px wide in an 1,152px panel. Squeezing everything into the panel instead is what I tried
   twice and it cost the Name column both times. */
const COL_W = {
  check: 36, no: 44, project: 96, name: 240, team: 120, skus: 76,
  // 160, not 140: the "Waiting: manager" badge measures 143 and a fixed column clips
  // rather than wraps, so three pixels short is a truncated badge.
  /* The tier column went on 2026-09-28 (Jay: "no need to show T1 or T2 ... on the Pricing Approval
     screen. Anyone who opens the quote can see them"), so `t1` is gone from here too — a width left
     behind in this table is what the colgroup silently reads for a column that no longer exists. */
  buying: 116, status: 160,
  /* Was one "Trail" column holding who, when, and every note stacked on top of each other (Max,
     2026-09-11: "cột Trail tách thành 2 cột là Submitted by và Note"). Who-and-when stays on one
     line each and does not wrap; the notes wrap, so that column is the wider of the two. */
  submitted: 200, note: 260,
  /* Who made it and who touched it last (Max, 2026-09-11). Names are given names, so 110 is
     generous; the dates are yyyy-mm-dd and never wrap. */
  createdBy: 110, createdAt: 112, updatedBy: 110, updatedAt: 112,
};

/* The Action column is measured from the buttons it will hold, not guessed — `.pa-sticky-cols td`
   clips its overflow, so a column one button too narrow does not wrap, it truncates, and it does it
   without a scrollbar or an ellipsis to admit it.

   The buttons carry an icon and no label now (Max, 2026-09-11: "ko cần ghi title đầu... khi user đưa
   chuột lại thì sẽ hiện ra"), so every one of them is the same square and the width is a count. That
   also retires the class of bug this table kept producing — a label changing, or a wider label being
   rendered by a variant nobody added here. */
const ACTION_BTN_SIZE = 32;
const ACTION_GAP = 6;
const ACTION_CELL_PAD = 20;
const actionWidthFor = (count) => count * ACTION_BTN_SIZE
  + Math.max(0, count - 1) * ACTION_GAP + ACTION_CELL_PAD;
const stClass = (s) => `cp-st cp-st-${String(s || 'Draft').replace(/\s/g, '-').toLowerCase()}`;

/* One tab per STAGE of the gate, not per viewer (Max, 2026-09-10: "3 tab tương ứng với 3 mức...
   tạo nhiều tab quá cũng ko tốt"). The five before this asked "what do YOU owe", which meant the
   same quote moved between tabs depending on who was looking; a stage is a fact about the quote.

   A quote sent back sits in Pending, because that is where it is — with the person who has to change
   it — and the status badge on the row is what tells it from a fresh draft. Approved quotes leave
   the gate: they are finished work, and Saved projects lists them with the badge and the Revise
   button. */
/* People are called by their given name here, the same as in the mail (Max, 2026-09-11: "chỉ cần lấy
   tên ko cần họ"). Rendered rather than stored: submitted_by keeps the whole name because it is the
   audit trail, and rows written before this still read right. */
const given = (name) => String(name || '').trim().split(/\s+/)[0] || '';

/* What the Sales OP column prints — Jay, 2026-09-16: '"Team 2" i/o T2'. It was showing `team_key`,
   the storage key, which reads as a code to everyone who did not design the table.

   The column header already says "Sales OP", so the team's full name ("Sales OP Team 2") would say
   it twice; the prefix comes off and the column reads "Team 2". Anything that is not a Sales OP team
   keeps its whole name, and a team with no name at all falls back to the key rather than to an empty
   cell — a blank would read as "no team", which is a different fact and already has its own dash. */
const teamLabel = (row) => {
  const name = String(row?.team_name || '').trim();
  if (!name) return String(row?.team_key || '').trim();
  return name.replace(/^Sales\s*OP\s+/i, '');
};

/* THE CALCULATION STEP (Max, 04/Nov/2026): Costing -> Calculation (Amy: HPAC Overhead % and T2 Markup % per item) -> sale
   manager -> CEO, on a CBD quote. Build-time, like the gate; the backend half is PRICING_CALC_STEP=1. */
const CALC_ON = process.env.REACT_APP_PRICING_CALC === '1';
const TABS = [
  { key: 'pending', statuses: ['Draft', 'Rejected'], label: 'pricing.stagePending', note: 'pricing.stagePendingHint' },
  ...(CALC_ON ? [{ key: 'calc', statuses: ['Pending calculation'], label: 'pricing.stageCalc', note: 'pricing.stageCalcHint' }] : []),
  { key: 'manager', statuses: ['Pending manager'], label: 'pricing.stageManager', note: 'pricing.stageManagerHint' },
  { key: 'final', statuses: ['Pending CEO'], label: 'pricing.stageFinal', note: 'pricing.stageFinalHint' },
  { key: 'done', statuses: ['Approved'], label: 'pricing.stageDone', note: 'pricing.stageDoneHint' },
];

/* THE GUIDE (Max 01/11: every module has one). The row steps are skipped by the tour when the stage shown is empty. */
const TOUR = [
  { selector: '.pa-tabs', title: 'tourPricingApproval.tabsT', body: 'tourPricingApproval.tabsB' },
  { selector: '.pa-toolbar', title: 'tourPricingApproval.toolbarT', body: 'tourPricingApproval.toolbarB' },
  { selector: '.pa-table-scroll table', title: 'tourPricingApproval.listT', body: 'tourPricingApproval.listB' },
  { selector: '.pa-table-scroll tbody td.pa-actions-col', title: 'tourPricingApproval.actionsT', body: 'tourPricingApproval.actionsB' },
];

export default function PricingApproval() {
  const { t } = useTranslation();
  /* WHAT THE CEO'S SCREEN HOLDS — Jay, 2026-09-17, on a screenshot taken in that role: "Young should
     NOT see Pending / Manager approval. He only needs to see his approvals (2)".

     The three tabs are the three STAGES of the gate, and the first two are somebody else's move: a
     draft is with the person writing it and the manager stage is with the team's manager. The CEO
     is the last gate, so a list of nine quotes he cannot act on is nine rows between him and the two
     he can.

     Read from useAuth, not from the `me` union the endpoint returns, because only useAuth follows
     the header's role switcher — the same reason the calculator's CEO view reads it there. It is a
     VIEW, not a permission: what a person may decide is `canDecide` below and the endpoints
     enforce it either way. An admin who holds the CEO role sees this too when they switch into it,
     which is how Jay took the screenshot. */
  const { roles: myRoles = [], activeRole, loading: roleLoading } = useAuth();
  const actingAs = (key) => (activeRole ? activeRole === key
    : myRoles.length === 1 && myRoles[0] === key);
  const isCeo = actingAs('CEO');
  const tabs = useMemo(() => (isCeo ? TABS.filter((tb) => tb.key === 'final' || tb.key === 'done') : TABS), [isCeo]);
  /* NOT KNOWN YET IS NOT "NOT THE CEO" — Max, 2026-09-17: "1s đầu sẽ thấy 3 tab rùi sau đó chỉ còn
     1 tab". `useAuth` only honours the stored active role once the account's role list has arrived
     (`roles.includes(activeRole)`, which is right — nobody should act as a role they do not hold) —
     and until then `roles` is empty, so a CEO reads as "not a CEO" and the full bar is painted for
     about 300ms before it collapses to one tab.
     Measured, cold: t+300ms three tabs, t+600ms one. Drawing nothing for that moment is honest;
     drawing the other two and taking them away is the screen telling somebody about work that is
     not theirs and then changing its mind. */
  const tabsKnown = !roleLoading;
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  /* The tab can arrive in the URL — the CEO and Sales OP home cards link straight to the list they
     counted, and landing on Pending when the card said Rejected is landing on the wrong list
     (Max, 2026-09-15). Read once, at mount: this is where somebody ARRIVES, not a filter that keeps
     being reapplied while they click around. */
  const [tab, setTab] = useState(() => {
    const want = new URLSearchParams(window.location.search).get('tab');
    /* CHECKED AGAINST THE TABS THAT EXIST, 2026-10-04. The list here read
       ['pending','approved','rejected'] and the tabs are pending / manager / final / done, so
       every link but ?tab=pending fell through to Pending — including the one in the approval mail,
       which points at the stage the quote is actually AT. A manager opening their own mail landed
       on a list their quote was not in. Found while driving the SKU work, not reported. */
    return TABS.some((x) => x.key === want) ? want : 'pending';
  });
  // The quote the mail pointed at, marked until it has been noticed.
  const [highlight, setHighlight] = useState('');
  const [teamScope, setTeamScope] = useState('');
  const [busyId, setBusyId] = useState('');
  // The decision being taken, and the approver's words. Same shape Project Approval passes.
  const [decisionFor, setDecisionFor] = useState(null);
  /* What the approver has crossed off inside the dialog, kept here so the read-back under it can be
     priced. The dialog owns the marks; this is the dialog telling us what they add up to. */
  const [decisionMarks, setDecisionMarks] = useState(null);
  /* Which SKUs the approver actually opened before pressing Approve. The panel owns it — it owns
     the tabs — and hands it over at the moment the decision starts, so the dialog can say which
     rows are about to be approved unread. */
  const [decisionSeen, setDecisionSeen] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  // The covering note the submitter writes for the approver, and the delete confirmation.
  const [submitting, setSubmitting] = useState(null);
  const [recalling, setRecalling] = useState(null);
  const [reminding, setReminding] = useState(null);   // { id, name, stage, note, soonAt }
  const [confirmDel, setConfirmDel] = useState(null);
  // Bulk selection, the way Project Approval does it: the boxes appear only on a stage where this
  // person can actually decide, so nobody ticks rows to find there is no button for them.
  const [selected, setSelected] = useState([]);
  /* WHICH NOTES ARE OPEN — Max, 2026-09-17: "click vào note thì sẽ mở rộng row ra tương tự như bên
     project approval nhé". Keyed per NOTE, not per row: a quote can carry the submitter's note, the
     manager's and the CEO's at once, and opening one to read it must not shove the others about.
     The same shape Project Approval keeps, so the two screens behave identically. */
  const [openNotes, setOpenNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [listRes, meRes] = await Promise.all([
        fetch(API, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/pricing/approval-context`, { credentials: 'include' }),
      ]);
      const list = await listRes.json();
      // i18n.t, not the hook's `t`: this callback is memoised once and must not hold a stale language.
      if (!listRes.ok) throw new Error(list.error || i18n.t('xPricing.loadFailed', { status: listRes.status }));
      setRows(list.rows || []);
      setMe(meRes.ok ? await meRes.json() : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  /* The approval mail links straight at one quote, so the screen has to open where that quote is:
     its stage's tab, scrolled to, and marked for a moment so the eye finds it in a full table (Max,
     2026-09-11). Runs once, after the rows arrive — the tab cannot be chosen before the quote's
     status is known — and the query string is dropped so a refresh is a normal visit. */
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || !rows.length) return;
    const want = new URLSearchParams(window.location.search).get('open');
    if (!want) { landed.current = true; return; }
    landed.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    const row = rows.find((r) => r.project_id === want);
    if (!row) { setMessage(t('pricing.openMissing', { id: want })); return; }
    const stage = tabs.find((x) => x.statuses.includes(row.approval_status || 'Draft'));
    if (stage) setTab(stage.key);
    setHighlight(want);
    // Long enough to be seen on a page that is still settling, short enough not to become decoration.
    window.setTimeout(() => setHighlight(''), 6000);
  }, [rows, t]);

  /* A tab that is no longer on the bar cannot be the open one. The default is `pending`, the URL
     can carry one, and the mail link picks the stage of the quote it points at — all three can land
     a CEO on a tab their own screen does not draw, and the table would simply come up empty with no
     tab lit to explain why. */
  useEffect(() => {
    if (tabs.some((tb) => tb.key === tab)) return;
    setTab(tabs[0].key);
  }, [tabs, tab]);

  useEffect(() => {
    if (!highlight) return;
    const el = document.querySelector(`tr[data-quote="${highlight}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlight, tab]);

  /* What this person may do to THIS quote, decided the way the server decides it. A screen offering
     a button the server refuses teaches people to ignore the screen. */
  const canApprove = useCallback((r) => {
    if (!me) return false;
    if (r.approval_status === 'Pending manager') {
      if (me.isAdmin) return true;
      /* A named first approver (2026-10-22) decides the first step for every team, and the team's
         own manager no longer does — the server's cpMayDecideFirst. An older server sends no
         `firstApprover`, and the team rule below is what it still enforces. */
      if (me.firstApprover?.set) return !!me.firstApprover.isMe;
      return !!(r.team_key && (me.managerTeams || []).includes(r.team_key));
    }
    if (r.approval_status === 'Pending CEO') return me.isAdmin || me.canApproveFinal;
    return false;
  }, [me]);

  const queue = useMemo(() => Object.fromEntries(tabs.map((tb) => [
    tb.key, rows.filter((r) => tb.statuses.includes(r.approval_status || 'Draft')),
  ])), [rows]);

  /* Always Pending (Max, 2026-09-11). It used to open on the first stage this person could act on,
     which meant the screen greeted different people with different tabs and nobody could say where
     they were without reading the highlight. The counts on the other two say whether there is
     anything there. */

  /* The dropdown filters by the KEY and shows the LABEL. Keeping the key as the value means the
     filter goes on matching the row even if two teams ever end up with the same printed name. */
  const teams = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => { if (r.team_key && !seen.has(r.team_key)) seen.set(r.team_key, teamLabel(r)); });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const visible = useMemo(() => {
    const list = queue[tab] || [];
    return teamScope ? list.filter((r) => r.team_key === teamScope) : list;
  }, [queue, tab, teamScope]);

  const act = async (id, action, body) => {
    setBusyId(id); setMessage(''); setError('');
    try {
      const res = await fetch(`${API}/${id}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.actionFailed', { action, status: res.status }));
      setMessage(data.projectId
        ? t('pricing.revisedInto', { id: data.projectId })
        : t('pricing.nowStatus', { status: t(stKey(data.status)), to: data.to || '' }));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId('');
    }
  };

  /* may this reader do the calculation step of this quote (the server's own test, approval-context `calc`) */
  const mayCalc = (r) => CALC_ON && !!me?.calc?.mayDo && r?.approval_status === 'Pending calculation';
  const showCheck = visible.some(canApprove);
  /* The widest button set any visible row will render, so one row with an extra button cannot clip
     every other row's. Derived from the same conditions actionsFor uses, rather than from a second
     list that could drift from it. */
  const actionCount = (() => {
    let n = 0;
    // Edit, Submit, Duplicate, Delete on anything not yet in the gate.
    if (visible.some((r) => ['Draft', 'Rejected'].includes(r.approval_status || 'Draft'))) n += 4;
    if (visible.some(canApprove)) n += 1;
    if (visible.some(mayCalc)) n += 1;              // Calculate (the calculation step, 04/11)
    /* Recall, added 2026-09-16 — and this line is the whole reason this count exists. The button
       shipped without it and hung outside the frozen column, half of it cut off: `.pa-sticky-cols td`
       clips rather than wraps, so a column one button too narrow truncates silently, with no
       scrollbar and no ellipsis to admit it. Any button added to actionsFor needs a line here. */
    if (visible.some((r) => r.canRecall)) n += 1;
    if (visible.some((r) => r.canRemind)) n += 1;   // Remind (Amy, FB-0003)
    return Math.max(1, n);
  })();
  const ACTION_W = actionWidthFor(actionCount);
  const GRID_W = Object.entries(COL_W)
    .filter(([k]) => k !== 'check' || showCheck)
    .reduce((a, [, w]) => a + w, 0) + ACTION_W;
  const selectedHere = selected.filter((id) => visible.some((r) => r.project_id === id && canApprove(r)));
  const allTicked = showCheck && visible.filter(canApprove).every((r) => selected.includes(r.project_id));
  const toggleAll = () => setSelected(allTicked ? [] : visible.filter(canApprove).map((r) => r.project_id));
  /* One at a time, in order, and it stops at the first refusal: a bulk that half-succeeds and says
     "done" is worse than one that says which quote it stopped on. */
  const runDecision = async (ids, action, note, skus) => {
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await act(id, action, action === 'reject'
        ? { reason: note }
        /* The per-SKU verdicts ride with the approval. Only on an approval: a rejection is about
           the whole quote and carries one reason, which is the shape it has always had. */
        : { ...(note ? { note } : {}), ...(skus && skus.length ? { skus } : {}) });
    }
    setSelected([]);
  };

  /* THE QUOTE'S SKUs, AS ROWS THE DECISION DIALOG ALREADY KNOWS HOW TO DRAW — Jay, 2026-10-04:
     "làm sao để cho phép chỉ duyệt 1 SKU", and the summary at the moment of pressing Approve.
     Both already exist: Project Approval has decided per SKU in this dialog since 2026-09-18, with
     a cross per row, a reason required on each one and a button that reads back "Approve 6 ·
     Reject 2". Building a second mechanism beside it is how one screen comes to ask for a reason
     in a way the other does not.

     ONLY WHEN THERE IS MORE THAN ONE. On a single-SKU quote the SKU IS the quote, and a dialog
     asking the same question twice is a dialog nobody reads.

     A SKU ALREADY REFUSED AT THE MANAGER STEP IS NOT PUT TO THE CEO AGAIN — it arrives as
     `Rejected` and the dialog draws it as settled, which is the rule it already follows. */
  /* `seen` is PASSED IN, not read off state. It is set in the same handler that builds these
     rows, and a state setter does not change the value this render is closed over — so reading
     `decisionSeen` here returned the previous one and every row came up unmarked while the summary
     below, which renders after the update, had it right. */
  const padSkuRows = (seen) => {
    const skus = detail?.snapshot?.payload?.skus || [];
    if (skus.length < 2) return null;
    const byIndex = {};
    (detail.skuDecisions || []).forEach((d) => { byIndex[d.sku_index] = d; });
    return skus.map((sk, i) => ({
      projectId: `#${i + 1}`,
      index: i,
      itemName: sk.name || `SKU ${i + 1}`,
      approvalStatus: byIndex[i]?.decision === 'rejected' ? 'Rejected' : 'Pending',
      decisionNote: byIndex[i]?.reason || '',
      /* MARKED HERE TOO — Max, 2026-10-05: "cần mark lại là phiếu chưa mở trong bảng summary
         luôn nhé". The tab strip says which SKUs have not been opened; this dialog is where they
         are approved, and a row about to be agreed to unread should say so where the agreeing
         happens rather than two inches above it behind an overlay. */
      flag: (seen && !seen.has(i)) ? t('pricing.padSkuUnseen') : '',
    }));
  };

  /* THE CARDS, RE-PRICED ON THE SKUs STILL IN — Max's call, 2026-10-04, when asked: "tính lại theo
     SKU còn được duyệt". A card folds every SKU of the quote, so once three of eight are crossed
     off the figures on the panel describe an order nobody is approving.
     RE-PRICED HERE AND NOT ON THE PANEL, which is the part worth keeping: on the panel the numbers
     would move under the approver while they were still reading, and a figure that changes while
     you look at it is a figure you stop trusting. In the dialog the marks ARE the subject, so the
     read-back is the one thing that should follow them. */
  const decisionSummary = () => {
    const rows = padSkuRows(decisionSeen);
    if (!rows || !detail) return null;
    const skus = detail.snapshot?.payload?.skus || [];
    const rules = detail.snapshot?.rules || null;
    const out = new Set();
    rows.forEach((r) => { if (r.approvalStatus === 'Rejected') out.add(r.index); });
    (decisionMarks || []).forEach((d) => {
      if (d.decision === 'Rejected') out.add(d.index); else out.delete(d.index);
    });
    const live = skus.filter((_, i) => !out.has(i));
    if (!live.length) {
      return <p className="pa-sum-none">{t('pricing.padSumNothing')}</p>;
    }
    const { cards } = cpCards(skus, rules, t, {}, live);
    const picked = cards.find((x) => x.key === detail.row.extra_option) || null;
    /* HOW MANY OF THE ONES BEING APPROVED WERE NEVER OPENED. The per-row mark says which; this
       says how many, at the point of commitment, because that is the number somebody would want
       to stop on. Silent when it is none. */
    const unread = decisionSeen
      ? skus.filter((_, i) => !out.has(i) && !decisionSeen.has(i)).length : 0;
    return (
      <>
        <b className="pa-sum-head">{t('pricing.padSumHead', { n: live.length, all: skus.length })}</b>
        {unread > 0 && <p className="pa-sum-unread">{t('pricing.padSumUnread', { n: unread })}</p>}
        {picked ? (
          <ul className="pa-sum-list">
            <li>
              <span>{t('pricing.padSumCard')}</span>
              <b>{picked.letter} · {picked.name.replace(/^[A-Z] \u00b7 /, '')}</b>
            </li>
            {/* The one figure the whole row is read on, recomputed for the SKUs that remain. The
                card's own name is beside it, because "net profit" with no card attached is a
                number about nothing. */}
            {!picked.hand && (
              <li>
                <span>{t('pricing.padNetProfit')}</span>
                <b className={picked.net < 0 ? 'bad' : 'good'}>{money(picked.net)}</b>
              </li>
            )}
            {!picked.hand && (
              <li>
                <span>{t('pricing.padSumQty')}</span>
                <b>{picked.qty} EA</b>
              </li>
            )}
          </ul>
        ) : (
          /* NOT A BLOCK, and deliberately so. Nothing in the flow says the extra has to be
             allocated before the quote is approved, and inventing that rule here would stop an
             approver who means to settle it in the Instruction box. It says what is missing. */
          <p className="pa-sum-nocard">{t('pricing.padSumNoCard')}</p>
        )}
      </>
    );
  };

  const removeQuote = async (id) => {
    setBusyId(id); setMessage(''); setError('');
    try {
      const res = await fetch(`${API}/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.deleteFailed', { status: res.status }));
      setMessage(t('pricing.deleted', { id }));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId('');
    }
  };

  // Open the quote in the calculator.
  /* THE ROW OPENS THE APPROVAL VIEW, not the calculator — Jay, 2026-09-29: "Pricing Approval is the
     manager and CEO screen... One link back to the calculator for anyone who wants the detail." It
     used to jump straight to the calculator (2026-09-16), which was right while this screen was only
     a list; now the decision lives here and the calculator is one click further on.
     The single-quote read is what carries the SNAPSHOT, so the panel draws the figures as submitted
     rather than as they stand now. */
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const openSubmit = (r) => setSubmitting({ id: r.project_id, name: r.name, note: '' });

  const openQuote = async (r) => {
    setDetailBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/projects/${encodeURIComponent(r.project_id)}`,
        { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xPricing.loadFailed', { status: res.status }));
      /* A DRAFT HAS NO SNAPSHOT because nothing has been submitted to freeze — Max, 2026-10-26, opening
         a test quote here and getting the "submitted before the freeze" sentence, which is about a
         different case. A draft is drawn from the quote as it stands, at its own rules, and the panel
         says so; a quote that WAS submitted without a snapshot still gets the honest sentence, since
         drawing that one live would show figures nobody approved. */
      const draft = !d.snapshot && (d.project?.approval_status || 'Draft') === 'Draft' && d.project?.payload;
      setDetail({
        row: { ...r, ...d.project },
        snapshot: d.snapshot || (draft ? { live: true, payload: d.project.payload, rules: d.rules || null } : null),
        /* The per-SKU verdicts already taken against this submission. They decide which SKUs the
           cards are priced on and which ones the decision dialog still has to ask about. */
        skuDecisions: d.skuDecisions || [],
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailBusy(false);
    }
  };

  const postDetail = async (path, body, okMsg) => {
    if (!detail) return;
    setDetailBusy(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/pricing/projects/${encodeURIComponent(detail.row.project_id)}/${path}`,
        { method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xPricing.failed', { status: res.status }));
      setMessage(okMsg);
      /* THE ONE QUOTE, NOT THE WHOLE LIST. Reloaded from the server rather than patched in place,
         because the decision and the memo both set who and when and a screen that guesses those
         would show a name the row does not carry — but `load()` is what tore the panel down and
         rebuilt it, which threw the approver back to block 1 after a button two screens down.
         Nothing on the grid prints the decision, so there is nothing in the list to refresh; it is
         refreshed on the next thing that changes a visible column. */
      await openQuote(detail.row);
    } catch (error) {
      setError(error.message);
    } finally {
      setDetailBusy(false);
    }
  };

  /* View is gone (Max, 2026-09-11). It went to the same screen Edit does — a quote waiting on
     somebody is locked against saving anyway — so it was a second door onto one room, and the
     project id in its own column is still that door for every row, including the ones with no
     Edit. */
  const actionsFor = (r) => {
    const out = [];
    if (['Draft', 'Rejected'].includes(r.approval_status || 'Draft')) {
      out.push(
        <button key="ed" type="button" className="pa-btn pa-iconbtn" onClick={() => openQuote(r)}
          title={t('pricing.edit')} aria-label={t('pricing.edit')}>
          <Pencil size={15} />
        </button>,
      );
    }
    return out.concat(stageActions(r));
  };

  const stageActions = (r) => {
    const busy = busyId === r.project_id;
    const out = [];
    if (mayCalc(r)) {
      out.push(
        <button key="calc" type="button" className="pa-btn approve pa-iconbtn" disabled={busy || detailBusy}
          title={t('pricing.calcOpen')} aria-label={t('pricing.calcOpen')} data-pa-calc={r.project_id}
          onClick={() => openQuote(r)}>
          <Calculator size={15} />
        </button>,
      );
    }
    if (canApprove(r)) {
      /* One button, not two. Approve and Send back were separate, so an approver who opened one
         and changed their mind had to close it and find the other; the decision now lives in the
         dialog with the Agree / Reject switch inside it. */
      out.push(
        <button key="ap" type="button" className="pa-btn approve pa-iconbtn" disabled={busy}
          title={t('xPricing.approve')} aria-label={t('xPricing.approve')}
          onClick={() => { setDecisionNote(''); setDecisionFor({ action: 'approve', group: { groupId: r.project_id, groupName: r.name } }); }}>
          <CheckCircle2 size={15} />
        </button>,
      );
    }
    /* PULL IT BACK — Christine via Jay, 2026-09-16: "Please add 'recall/collect' or 'edit' button so
       that you can void the request before YL approves it." Whether it is offered is the SERVER'S
       answer (`canRecall` comes back with the row), not a rule worked out again here: the endpoint
       refuses on the same test, and a button that appears and then refuses is worse than no button.
       It sits after Approve because on a quote waiting on you, deciding comes first. */
    if (r.canRecall) {
      out.push(
        <button key="rc" type="button" className="pa-btn pa-iconbtn" disabled={busy}
          title={t('pricing.recall')} aria-label={t('pricing.recall')}
          onClick={() => setRecalling({ id: r.project_id, name: r.name, stage: r.approval_status, note: '' })}>
          <Undo2 size={15} />
        </button>,
      );
    }
    /* NUDGE WHOEVER HOLDS IT — Amy, FB-0003 (04/11): "add thêm nút remind costing for approval". Offered when the server says
       so (`canRemind`: the author, the team or an admin, never the person it waits on); one mail per quote every 4 hours. */
    if (r.canRemind) {
      out.push(
        <button key="rm" type="button" className="pa-btn pa-iconbtn" disabled={busy}
          title={t('pricing.remind')} aria-label={t('pricing.remind')} data-pa-remind={r.project_id}
          onClick={() => setReminding({ id: r.project_id, name: r.name, stage: r.approval_status, note: '',
            soonAt: r.remindNextAt && new Date(r.remindNextAt) > new Date() ? r.remindNextAt : null, by: r.remind_by })}>
          <BellRing size={15} />
        </button>,
      );
    }
    if (['Draft', 'Rejected'].includes(r.approval_status || 'Draft')) {
      out.push(
        /* Resubmit gets its OWN icon, not Send again. With labels gone these two are told apart by
           the picture alone, and the same paper plane on both would have made a first submission and
           a second look identical (Max, 2026-09-11). */
        <button key="sb" type="button" className="pa-btn submit pa-iconbtn" disabled={busy}
          title={r.approval_status === 'Rejected' ? t('pricing.resubmit') : t('pricing.submit')}
          aria-label={r.approval_status === 'Rejected' ? t('pricing.resubmit') : t('pricing.submit')}
          onClick={() => openSubmit(r)}>
          {r.approval_status === 'Rejected' ? <RotateCw size={15} /> : <Send size={15} />}
        </button>,
        <button key="dp" type="button" className="pa-btn pa-iconbtn" disabled={busy}
          title={t('pricing.duplicate')} aria-label={t('pricing.duplicate')}
          onClick={() => act(r.project_id, 'duplicate')}>
          <Copy size={15} />
        </button>,
        <button key="dl" type="button" className="pa-btn danger pa-iconbtn" disabled={busy}
          title={t('pricing.del')} aria-label={t('pricing.del')}
          onClick={() => setConfirmDel({ id: r.project_id, name: r.name })}>
          <Trash2 size={15} />
        </button>,
      );
    }
    return out;
  };

  /* The ROLE, not the person (Max, 2026-09-11). It used to read "Christine — approval" from the
     server's answer about who holds the level, which is true but dates the screen the moment either
     of them moves on — and the tabs are a description of the gate, not of a rota. */
  const stageLabel = (tb) => t(tb.label);
  /* who holds a quote at its stage, for the recall and reminder sentences */
  const holderName = (stage) => (stage === 'Pending CEO' ? (me?.finalName || t('pricing.stageFinal'))
    : stage === 'Pending calculation' ? (me?.calc?.name || t('pricing.stageCalc'))
      : (me?.managerName || t('pricing.stageManager')));
  /* THE CALCULATION STEP'S MOVES, from inside the quote. Save keeps the popup open on the same quote; Send and Return
     move it to another stage, so the popup closes and the list is read again. */
  const calcPost = async (path, body) => {
    const res = await fetch(`${BACKEND_URL}/api/pricing/projects/${encodeURIComponent(detail.row.project_id)}/${path}`,
      { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const d = await res.json();
    if (!res.ok) throw new Error(d.error || t('xPricing.failed', { status: res.status }));
    return d;
  };
  const calcMove = async (steps, okMsg) => {
    setDetailBusy(true); setError(''); setMessage('');
    try {
      let last = null;
      for (const [path, body] of steps) {
        // eslint-disable-next-line no-await-in-loop
        last = await calcPost(path, body);
      }
      setDetail(null);
      setMessage(typeof okMsg === 'function' ? okMsg(last) : okMsg);
      await load();
    } catch (err) {
      setError(err.message);
      // the popup stays open on a refusal, and says why itself
      setMessage(err.message);
    } finally {
      setDetailBusy(false);
    }
  };

  return (
    <div className="pa-page">
      <Header />
      <main className="pa-main">
        <ModuleTabs module="pricing" />
        {/* Project Approval's own title block: a plain h2 and one line under it. The shield icon
            this had was the first thing that made the two screens read as different products. */}
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('pricing.approvalTitle')}</h2>
            <p>{t('pricing.approvalSub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="pricingApproval" ready={!loading && !roleLoading} />
        </div>


        {!!error && <div className="pa-banner err">{error}</div>}
        {!!message && <div className="pa-banner ok">{message}</div>}

        {/* The bar keeps its place while the role is unknown, so nothing below it jumps when the
            tabs arrive — the flash being fixed is a CHANGE of content, and replacing it with a
            change of height would only move the problem down the page. */}
        <div className="pa-tabs">
          {tabsKnown && tabs.map((tb) => (
            <button key={tb.key} type="button"
              className={`pa-tab ${tab === tb.key ? 'active' : ''}`}
              onClick={() => setTab(tb.key)}>
              {stageLabel(tb)}
              <span className="pa-count">{(queue[tb.key] || []).length}</span>
            </button>
          ))}
        </div>

        {loading && <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('pricing.loading')}</div>}

        {!loading && (
          <div className="pa-section">
            {/* No heading repeating the tab that is already lit, and no Reload button: Project
                Approval has neither, and a screen people move between should not grow furniture the
                other one does not have. The count and the scope filter are its whole toolbar. */}
            <div className="pa-toolbar">
              <span className="pa-count-text">
                {visible.length === (queue[tab] || []).length
                  ? t('pricing.countAll', { count: visible.length })
                  : t('pricing.countOf', { n: visible.length, total: (queue[tab] || []).length })}
              </span>
              {teams.length > 1 && (
                <select className="pa-scope" value={teamScope} onChange={(e) => setTeamScope(e.target.value)}>
                  <option value="">{t('pricing.allTeams')}</option>
                  {teams.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              )}
              {/* The bulk bar appears only when something is ticked, so the normal view stays quiet —
                  the same rule as Project Approval's. */}
              {!!selectedHere.length && (
                <div className="pa-bulk">
                  <strong>{t('pricing.selected', { n: selectedHere.length })}</strong>
                  <button type="button" className="pa-btn approve" disabled={!!busyId}
                    onClick={() => { setDecisionNote(''); setDecisionFor({ mode: 'bulk', action: 'approve' }); }}>
                    <CheckCircle2 size={14} /> {t('pricing.approveSelected')}
                  </button>
                  <button type="button" className="pa-btn" onClick={() => setSelected([])}>
                    {t('pricing.clearSelection')}
                  </button>
                </div>
              )}
            </div>

            {/* THE APPROVER'S VIEW, above the list it was opened from, and OUTSIDE both the
                `visible.length` guard and the grid's own 585px scroller. Inside the guard it
                vanished when a filter emptied the list under an open quote; inside the scroller the
                approver was reading 1,100px of decision through a 585px window. */}
            {!!detail && (
              <PricingApprovalDetail
                row={detail.row}
                snapshot={detail.snapshot}
                skuDecisions={detail.skuDecisions}
                canDecide={!!(me && (me.canApproveFinal || me.isAdmin))}
                busy={detailBusy}
                /* The page's own banner sits BEHIND the overlay; the panel draws it itself. */
                flash={message}
                onClose={() => setDetail(null)}
                onDecide={(option, instruction) => postDetail('extra-decision',
                  { option, instruction }, t('pricing.padDecisionSaved'))}
                /* APPROVE AND REJECT FROM INSIDE THE QUOTE — Max, 2026-10-01: "thêm nút approve,
                   reject hoặc cancel. Nếu click approve hay reject thì có thêm popup reason hay
                   note nhé". They were only in the list's Action column, so the person who had just
                   read five screens of figures had to close the quote and find its row again to act
                   on it. The SAME `DecisionDialog` the list opens, not a second one — a copy is how
                   two screens come to ask for a reason in two different ways. */
                canGate={canApprove(detail.row)}
                onGate={(action, seen) => {
                  setDecisionMarks(null);
                  setDecisionSeen(seen instanceof Set ? seen : null);
                  setDecisionFor({
                    mode: 'row', action, from: 'pad',
                    group: {
                      groupId: detail.row.project_id,
                      groupName: detail.row.name,
                      /* The quote's own SKUs, so the approver can take some and refuse others
                         without leaving the dialog. Null on a one-SKU quote, which is the dialog's
                         own test for drawing the per-SKU list at all. */
                      skuRows: padSkuRows(seen instanceof Set ? seen : null),
                    },
                  });
                }}
                onMemo={(memo) => postDetail('sop-memo', { memo }, t('pricing.padMemoSaved'))}
                canCalc={mayCalc(detail.row)}
                calcBy={me?.calc?.name || ''}
                onCalcSave={(body) => postDetail('calc', body, t('pricing.calcSaved'))}
                onCalcSend={(note, body) => calcMove([...(body ? [['calc', body]] : []), ['calc-send', { note }]],
                  (d) => t('pricing.calcSent', { id: detail.row.project_id, status: t(stKey(d?.status)) }))}
                onCalcReturn={(reason) => calcMove([['calc-return', { reason }]], t('pricing.calcReturned', { id: detail.row.project_id }))}
              />
            )}

            {!visible.length && <div className="pa-empty">{t('pricing.queueEmpty')}</div>}

            {!!visible.length && (
              <div className="pa-table-wrap pa-table-scroll">
                {/* Declared widths, the way Project Approval declares its own. Left to the browser
                    the identity columns lose to the wide ones and "CP-000010" breaks across two
                    lines — an id that wraps is an id you cannot read at a glance. */}
                {/* Every width declared and the table given their sum, so it scrolls sideways
                    rather than squeezing a column to nothing — Project Approval's own arrangement.
                    Declaring all but one and letting the browser take the remainder is what turned
                    "Helinox China Launching" into five stacked syllables: the declared columns added
                    up to more than the panel, so the remainder was 60px. */}
                <table
                  className={`pa-table pa-sticky-cols ${showCheck ? 'has-check' : ''}`}
                  style={{ width: '100%', minWidth: GRID_W }}
                >
                  <colgroup>
                    {showCheck && <col style={{ width: COL_W.check }} />}
                    {['no', 'project', 'name', 'team', 'skus', 'buying', 'status', 'submitted', 'note',
                      'createdBy', 'createdAt', 'updatedBy', 'updatedAt']
                      .map((k) => <col key={k} style={{ width: COL_W[k] }} />)}
                    <col style={{ width: ACTION_W }} />
                    {/* Soaks up whatever is left when the table is narrower than its panel. Without
                        it the header bar stopped at Action and the rest of the panel showed through
                        white (Max's screenshot, 2026-09-11). The other columns keep their declared
                        widths, which the frozen offsets depend on. */}
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      {showCheck && (
                        <th className="pa-check-col">
                          <input type="checkbox" checked={allTicked} onChange={toggleAll} aria-label={t('xPricing.selectAll')} />
                        </th>
                      )}
                      <th className="pa-no-col">{t('pricing.colNo')}</th>
                      <th className="pa-freeze">{t('pricing.colProject')}</th>
                      <th className="pa-freeze">{t('pricing.colName')}</th>
                      <th>{t('pricing.colTeam')}</th>
                      <th className="num">{t('pricing.colSkus')}</th>
                      <th className="num">{t('pricing.colBuying')}</th>
                      <th>{t('pricing.colStatus')}</th>
                      <th>{t('pricing.colSubmittedBy')}</th>
                      <th>{t('pricing.colNote')}</th>
                      <th>{t('pricing.colCreatedBy')}</th>
                      <th>{t('pricing.colCreatedAt')}</th>
                      <th>{t('pricing.colModifiedBy')}</th>
                      <th>{t('pricing.colModifiedAt')}</th>
                      <th className="pa-actions-col">{t('pricing.colAction')}</th>
                      <th className="pa-filler" aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r, i) => (
                      <tr
                        key={r.project_id}
                        data-quote={r.project_id}
                        className={`${selected.includes(r.project_id) ? 'on' : ''}`
                          + `${highlight === r.project_id ? ' pa-landed' : ''}`}
                      >
                        {showCheck && (
                          <td className="pa-check-col">
                            <input
                              type="checkbox"
                              checked={selected.includes(r.project_id)}
                              onChange={() => setSelected((prev) => (prev.includes(r.project_id)
                                ? prev.filter((x) => x !== r.project_id) : [...prev, r.project_id]))}
                              aria-label={r.project_id}
                            />
                          </td>
                        )}
                        <td className="pa-no-col">{i + 1}</td>
                        <td className="pa-freeze">
                          <button type="button" className="pa-link" onClick={() => openQuote(r)}>
                            {r.project_id}
                          </button>
                        </td>
                        {/* The NAME opens the quote too — Jay, 2026-09-16: "Please add hyperlink in
                            the title as well". The id beside it has been the only door since the View
                            button came off, and the id is not what anybody reads the row by. Same
                            target on purpose: two doors onto one room beat a second destination. */}
                        <td className="pa-name pa-freeze">
                          <button type="button" className="pa-link pa-name-link" onClick={() => openQuote(r)}
                            title={t('pricing.openQuote', { id: r.project_id })}>
                            {r.name}
                          </button>
                        </td>
                        <td>{teamLabel(r) || <span className="pa-muted">—</span>}</td>
                        <td className="num">{r.sku_count ?? r.entity_count ?? '—'}</td>
                        <td className="num">{fmtUSD(r.buying_cost)}</td>
                        <td>
                          <span className={stClass(r.approval_status)}>{t(stKey(r.approval_status))}</span>
                          {r.remind_at && ['Pending manager', 'Pending CEO'].includes(r.approval_status) && (
                            <div className="pa-muted pa-reminded" title={t('pricing.remindCount', { n: r.remind_count || 1 })}>
                              {t('pricing.reminded', { who: given(r.remind_by), at: hm(r.remind_at) })}
                            </div>
                          )}
                        </td>
                        {/* Who has had it and when — one line per hand it has passed through. */}
                        <td className="pa-when">
                          {r.submitted_by && <div>{t('pricing.trailSubmitted', { who: given(r.submitted_by), on: day(r.submitted_at) })}</div>}
                          {r.manager_by && <div>{t('pricing.trailManager', { who: given(r.manager_by), on: day(r.manager_at) })}</div>}
                          {r.ceo_by && <div>{t('pricing.trailFinal', { who: given(r.ceo_by), on: day(r.ceo_at) })}</div>}
                          {!r.submitted_by && <div className="pa-muted">{t('pricing.trailNone')}</div>}
                        </td>
                        {/* And what each of them said. Their own column because it wraps and the
                            trail does not, and because stacking four kinds of line in one cell is
                            what made this unreadable. */}
                        <td className={`pa-notes-col${
                          [`${r.project_id}:submit`, `${r.project_id}:manager`, `${r.project_id}:ceo`,
                            `${r.project_id}:reject`].some((k) => openNotes[k]) ? ' on' : ''}`}>
                          {/* A BUTTON per note, not a div with a handler: this is the only way to
                              read the rest of a long one, so it has to be reachable by keyboard and
                              say whether it is open. The full text stays on `title` while it is
                              shut, so a hover still answers a short one without a click. */}
                          {[
                            ['submit', r.submit_note, t('pricing.noteFrom'), ''],
                            ['manager', r.manager_note, t('pricing.noteManager'), ''],
                            ['ceo', r.ceo_note, t('pricing.noteFinal'), ''],
                            ['reject', r.reject_reason,
                              r.reject_level === 'ceo' ? t('pricing.sentBackCeo')
                                : r.reject_level === 'calc' ? t('pricing.sentBackCalc') : t('pricing.sentBackMgr'),
                              ' pa-warn'],
                          ].filter(([, text]) => !!text).map(([kind, text, label, extra]) => {
                            const noteKey = `${r.project_id}:${kind}`;
                            const open = !!openNotes[noteKey];
                            return (
                              <button key={kind} type="button"
                                className={`pa-note${extra}${open ? ' on' : ''}`}
                                aria-expanded={open}
                                title={open ? undefined : text}
                                onClick={() => setOpenNotes((prev) => ({ ...prev, [noteKey]: !prev[noteKey] }))}
                              >
                                <b>{label}:</b> {text}
                              </button>
                            );
                          })}
                          {!r.submit_note && !r.manager_note && !r.ceo_note && !r.reject_reason && (
                            <span className="pa-muted">—</span>
                          )}
                        </td>
                        {/* The record of the row itself, after the record of the decision. Given
                            names and yyyy-mm-dd, the same as everywhere else on this screen. */}
                        <td className="pa-when">{given(r.created_by) || <span className="pa-muted">—</span>}</td>
                        <td className="pa-when">{day(r.created_at) || <span className="pa-muted">—</span>}</td>
                        <td className="pa-when">{given(r.updated_by) || <span className="pa-muted">—</span>}</td>
                        <td className="pa-when">{day(r.updated_at) || <span className="pa-muted">—</span>}</td>
                        <td className="pa-actions-col">
                          <div className="pa-actions">{actionsFor(r)}</div>
                        </td>
                        <td className="pa-filler" aria-hidden="true" />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Submit asks for a note first. The approver otherwise gets a table of numbers with nothing
          saying why this price or what changed — the rejection already carries a reason, and the
          request should too (Max, 2026-09-11). Optional: some quotes need no covering note, and
          forcing one would only produce "ok". */}
      {submitting && (
        <div className="pa-overlay" onClick={() => setSubmitting(null)}>
          <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('pricing.submitTitle', { id: submitting.id })}</h3>
            <p className="pa-modal-sub">{CALC_ON && me?.calc?.on && !me?.calc?.mayDo
              ? t('pricing.submitBodyCalc', { who: me.calc.name || t('pricing.stageCalc'), mgr: me?.managerName || t('pricing.stageManager') })
              : t('pricing.submitBody', { who: me?.managerName || t('pricing.stageManager') })}</p>
            <textarea
              className="pa-reject-note"
              rows={4}
              placeholder={t('pricing.submitPh')}
              value={submitting.note}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
              onChange={(e) => setSubmitting((x) => ({ ...x, note: e.target.value }))}
            />
            {/* NO COSTING CHOOSER — Max, 2026-09-30: "option và card là 1". The quote goes up
                with every card it carries, and the approver picks one of them; asking the sender
                to pick one first made the same decision twice, and the approver's screen then hid
                the ones the sender had not sent. The note is what this dialog is for. */}
            <div className="pa-modal-actions">
              <button type="button" className="pa-btn" onClick={() => setSubmitting(null)}>{t('pricing.cancel')}</button>
              <button type="button" className="pa-btn submit"
                onClick={() => { const x = submitting; setSubmitting(null); act(x.id, 'submit', { note: x.note.trim() }); }}>
                <Send size={14} /> {t('pricing.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* An optional note, unlike a rejection's required reason: a rejection is a decision about
          somebody else's work and owes them an explanation, while this is a person withdrawing their
          own. The dialog names who stops waiting, because that is the part easiest to forget. */}
      {recalling && (
        <div className="pa-overlay" onClick={() => setRecalling(null)}>
          <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('pricing.recallTitle', { id: recalling.id })}</h3>
            <p className="pa-modal-sub">
              {t('pricing.recallBody', {
                who: holderName(recalling.stage),
              })}
            </p>
            <textarea
              className="pa-reject-note"
              rows={3}
              placeholder={t('pricing.recallPh')}
              value={recalling.note}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
              onChange={(e) => setRecalling((x) => ({ ...x, note: e.target.value }))}
            />
            <div className="pa-modal-actions">
              <button type="button" className="pa-btn" onClick={() => setRecalling(null)}>{t('pricing.cancel')}</button>
              <button type="button" className="pa-btn submit"
                onClick={() => { const x = recalling; setRecalling(null); act(x.id, 'recall', { note: x.note.trim() }); }}>
                <Undo2 size={14} /> {t('pricing.recall')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The reminder: who gets it, an optional line of your own, and — inside the 4-hour gap — when the next one can go. */}
      {reminding && (
        <div className="pa-overlay" onClick={() => setReminding(null)}>
          <div className="pa-modal" onClick={(e) => e.stopPropagation()} data-pa-remindbox>
            <h3>{t('pricing.remindTitle', { id: reminding.id })}</h3>
            <p className="pa-modal-sub">
              {t('pricing.remindBody', {
                who: holderName(reminding.stage),
              })}
            </p>
            {reminding.soonAt && <p className="pa-modal-sub pa-warn">{t('pricing.remindSoon', { who: given(reminding.by), at: hm(reminding.soonAt) })}</p>}
            <textarea
              className="pa-reject-note"
              rows={3}
              maxLength={500}
              placeholder={t('pricing.remindPh')}
              value={reminding.note}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
              onChange={(e) => setReminding((x) => ({ ...x, note: e.target.value }))}
              data-pa-remindnote
            />
            <div className="pa-modal-actions">
              <button type="button" className="pa-btn" onClick={() => setReminding(null)}>{t('pricing.cancel')}</button>
              <button type="button" className="pa-btn submit" disabled={!!reminding.soonAt} data-pa-remindsend
                onClick={async () => {
                  const x = reminding; setReminding(null); setBusyId(x.id); setMessage(''); setError('');
                  try {
                    const res = await fetch(`${API}/${x.id}/remind`, { method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: x.note.trim() }) });
                    const d = await res.json();
                    if (!res.ok) throw new Error(d.error || t('xPricing.actionFailed', { action: 'remind', status: res.status }));
                    setMessage(t('pricing.remindSent', { id: x.id, to: d.to }));
                    await load();
                  } catch (err) { setError(err.message); } finally { setBusyId(''); }
                }}>
                <BellRing size={14} /> {t('pricing.remindSend')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="pa-overlay" onClick={() => setConfirmDel(null)}>
          <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('pricing.deleteTitle')}</h3>
            <p className="pa-modal-sub">{t('pricing.deleteBody', { id: confirmDel.id })}</p>
            <div className="pa-modal-actions">
              <button type="button" className="pa-btn" onClick={() => setConfirmDel(null)}>{t('pricing.cancel')}</button>
              <button type="button" className="pa-btn danger"
                onClick={() => { const x = confirmDel; setConfirmDel(null); removeQuote(x.id); }}>
                <Trash2 size={14} /> {t('pricing.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {decisionFor && (
        <DecisionDialog
          spec={decisionFor}
          note={decisionNote}
          onNote={setDecisionNote}
          busy={decisionFor.mode === 'bulk' ? !!busyId : busyId === decisionFor.group?.groupId}
          selectedIds={selectedHere}
          onSwitch={(action) => setDecisionFor((d) => ({ ...d, action }))}
          onCancel={() => {
            setDecisionFor(null); setDecisionNote(''); setDecisionMarks(null); setDecisionSeen(null); setDecisionSeen(null);
          }}
          overlayClass={decisionFor.from === 'pad' ? 'over-pad' : ''}
          /* Only the quote panel's own approval re-prices itself: the list's bulk decision has no
             one quote to price and Project Approval passes neither of these. */
          summary={decisionFor.from === 'pad' && decisionFor.action === 'approve'
            ? decisionSummary() : null}
          onMarks={decisionFor.from === 'pad' ? setDecisionMarks : undefined}
          onConfirm={async (decided) => {
            const { mode, action, group, from } = decisionFor;
            const note = decisionNote.trim();
            setDecisionFor(null); setDecisionNote(''); setDecisionMarks(null); setDecisionSeen(null);
            const ids = mode === 'bulk' ? selectedHere : [group.groupId];
            /* `decided` is the dialog's read-back: one entry per SKU still awaiting an answer,
               each carrying the position the caller put on the row.
               THE ENDPOINT FOLLOWS THE ROWS, NOT THE BUTTON THAT OPENED THE DIALOG — the rule
               Project Approval has followed since 2026-09-18. An approver can open Approve and
               cross every SKU, and the button then reads "Reject 3": sending that to /approve
               earns a refusal about approving, which is the screen arguing with itself. When
               nothing is left the quote is being sent back, so it goes to /reject, and the per-SKU
               reasons travel in the rejection rather than being thrown away and retyped. */
            const marks = Array.isArray(decided) ? decided : null;
            const allOut = marks && marks.length && marks.every((d) => d.decision === 'Rejected');
            const realAction = allOut ? 'reject' : action;
            const realNote = allOut
              ? [note, ...marks.map((d) => `${d.name || d.projectId}: ${d.note}`)]
                .filter(Boolean).join('\n')
              : note;
            await runDecision(ids, realAction, realNote,
              realAction === 'approve' && marks ? marks : null);
            /* Opened from the quote panel, the panel has to be told: it is still on screen showing
               the status the quote had a moment ago. */
            if (from === 'pad' && detail) await openQuote(detail.row);
          }}
        />
      )}
    </div>
  );
}
